import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

interface Click {
  id: number;
  created_at: string;
  shop_name: string;
  shop_listing_id: number;
  listing_name: string | null;
  game_id: number;
  game_title: string | null;
  user_id: number | null;
  user_email: string | null;
  session_id: string | null;
  referer: string | null;
  user_agent: string | null;
}

interface HistoryResponse {
  listing: {
    id: number;
    shop_name: string;
    listing_name: string | null;
    listing_url: string;
    game_id: number | null;
    game_title: string | null;
  };
  totals: {
    total_clicks: number;
    unique_users: number;
    unique_sessions: number;
  };
  clicks: Click[];
}

function fetchWithAuth(url: string) {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

export function AdminShopClickHistoryPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/admin/shop_clicks/${id}`);
      const json = await res.json();
      setData(json);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = (iso: string) => new Date(iso).toLocaleString("lt-LT");

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link
        to="/admin/shop_clicks"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        ← Parduotuvių paspaudimai
      </Link>

      {loading || !data ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          <h1 className="text-2xl font-bold text-gray-900">
            {data.listing.shop_name}
            {data.listing.listing_name && (
              <span className="ml-2 text-base font-normal text-gray-500">
                — {data.listing.listing_name}
              </span>
            )}
          </h1>
          {data.listing.game_id && (
            <p className="mt-1 text-sm text-gray-600">
              Žaidimas:{" "}
              <Link to={`/games/${data.listing.game_id}`} className="text-blue-700 hover:underline">
                {data.listing.game_title || `#${data.listing.game_id}`}
              </Link>
            </p>
          )}
          <p className="mt-1 break-all text-xs text-gray-400 font-mono">
            {data.listing.listing_url}
          </p>

          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-gray-600">Paspaudimai</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{data.totals.total_clicks}</p>
            </div>
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm text-gray-600">Unikalūs naudotojai</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{data.totals.unique_users}</p>
            </div>
            <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
              <p className="text-sm text-gray-600">Unikalios sesijos</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{data.totals.unique_sessions}</p>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="mb-3 text-sm font-semibold text-gray-700">
              Visa istorija ({data.clicks.length})
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500">
                  <tr>
                    <th className="py-1">Laikas</th>
                    <th className="py-1">Naudotojas</th>
                    <th className="py-1">Sesija</th>
                    <th className="py-1">Šaltinis</th>
                    <th className="py-1">User-Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clicks.map((c) => (
                    <tr key={c.id} className="border-t border-gray-100">
                      <td className="py-1 text-gray-600 whitespace-nowrap">{formatDate(c.created_at)}</td>
                      <td className="py-1">
                        {c.user_email ? (
                          <span className="text-blue-700">{c.user_email}</span>
                        ) : (
                          <span className="text-gray-400 italic">anonim.</span>
                        )}
                      </td>
                      <td className="py-1 text-xs text-gray-400 font-mono">
                        {c.session_id ? c.session_id.slice(0, 12) : "—"}
                      </td>
                      <td className="py-1 max-w-[200px] truncate text-xs text-gray-500">
                        {c.referer || "—"}
                      </td>
                      <td className="py-1 max-w-[260px] truncate text-xs text-gray-400">
                        {c.user_agent || "—"}
                      </td>
                    </tr>
                  ))}
                  {data.clicks.length === 0 && (
                    <tr><td colSpan={5} className="py-3 text-center text-gray-400">Nėra paspaudimų</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
